import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import NamespaceHeader from './components/NamespaceHeader';
import StringGroupsTable from './components/StringGroupsTable';
import CreateStringGroupModal from './components/CreateStringGroupModal';
import { useNamespacePageData } from './hooks/useNamespacePageData';
import { useStringGroupsData } from './hooks/useStringGroupsData';
import Skeleton from '@amzn/awsui-components-console/skeleton';
import StackedBreadcrumbGroup from 'src/common/ui/StackedBreadcrumbGroup';
import AccessDeniedAlert from 'src/common/authz/AccessDeniedAlert';
import {
  getAccessDeniedMessage,
  getAccessDeniedNamespace,
  isAccessDenied,
} from 'src/common/authz/isAccessDenied';
import { useNamespacePermissions } from 'src/common/hooks/useNamespacePermissions';
import { CreateStringGroupFormData } from './types';
import type { NewStringGroupMetadata } from 'src/features/string-group-detail/types';
import { getUsernameFromJwt } from 'src/common/utils/auth';
import { buildStringGroupPath } from 'src/common/routes';
import { useTranslation } from 'src/i18n/shim/useTranslation';

const NamespaceDetailPage: React.FC = () => {
  const { namespaceId } = useParams<{ namespaceId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Route params are typed optional by react-router; the /ns/:namespaceId route
  // cannot match without one, so normalize once rather than coercing at each use.
  const resolvedNamespaceId = namespaceId ?? '';
  const pageData = useNamespacePageData(namespaceId);
  const stringGroupsData = useStringGroupsData(namespaceId);
  const permissions = useNamespacePermissions(namespaceId);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadedStringGroupIds = useMemo(
    () => stringGroupsData.displayItems.map((item) => item.stringGroupId),
    [stringGroupsData.displayItems],
  );

  const openCreateModal = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCreateStringGroup = useCallback(
    async (data: CreateStringGroupFormData): Promise<void> => {
      // TODO: Wire the create mutation (e.g. useCreateStringGroupMutation).
      // Nothing is persisted yet — the string group is NOT created on the
      // backend here, so the form's description, teamName, cxFamilyId and
      // sourceLocale survive only in the navigation state handed over below and
      // are lost on refresh. Creation is deferred until that mutation exists.
      // await createStringGroupMutation({ variables: { input: { ... } } });

      // Redirect to the (not yet persisted) group's detail page, carrying the
      // form data in navigation state. The detail page renders its metadata
      // from this handoff instead of fetching describeStringGroup, which has
      // nothing to return for a group the backend does not know about yet.
      const newGroupMetadata: NewStringGroupMetadata = {
        stringGroupId: data.name,
        namespaceId: resolvedNamespaceId,
        description: data.description,
        teamName: data.teamName,
        cxFamilyId: data.cxFamilyId,
        // Flatten Cloudscape option objects to plain values: navigation state
        // must be structured-clone serializable, and the detail page only
        // needs the ids.
        cxIds: data.cxIds
          .map((option) => option.value ?? '')
          .filter((value) => value !== ''),
        sourceLocale: data.sourceLocale?.value ?? '',
        createdOn: Date.now(),
        // Same source of truth AppHeader uses for the avatar: the Midway
        // alias from the JWT. Lets the prefilled metadata panel render
        // "18/08/2026 by buyyala" without a backend round trip.
        createdBy: getUsernameFromJwt() ?? '',
      };
      navigate(buildStringGroupPath(resolvedNamespaceId, data.name), {
        state: { newStringGroup: newGroupMetadata },
      });
    },
    [resolvedNamespaceId, navigate],
  );

  return (
    <SpaceBetween size="l">
      <StackedBreadcrumbGroup
        items={[
          // No href: this is the only item, so StackedBreadcrumbGroup renders it
          // as the inert current-page crumb after prepending its own root. An
          // href here would never drive navigation, and an unencoded one would
          // become a latent bug the moment the crumb did become clickable.
          {
            topText: t('namespace-breadcrumb-label'),
            bottomText: resolvedNamespaceId,
          },
        ]}
      />

      {/*
        A refusal gets the backend's own wording: the "Failed to load namespace"
        framing below would report it as a fault, when nothing failed.
      */}
      {pageData.namespaceError ? (
        isAccessDenied(pageData.namespaceError) ? (
          <AccessDeniedAlert
            message={getAccessDeniedMessage(pageData.namespaceError)}
            deniedNamespace={getAccessDeniedNamespace(pageData.namespaceError)}
            data-testid="namespace-access-denied"
          />
        ) : (
          <Alert type="error" header={t('namespace-load-error-header')}>
            {pageData.namespaceError.message}
          </Alert>
        )
      ) : pageData.namespaceLoading ? (
        <Box textAlign="center" padding="l">
          <Skeleton />
        </Box>
      ) : (
        <NamespaceHeader
          namespace={pageData.namespace}
          onCreateStringGroup={openCreateModal}
          canCreateStringGroup={permissions.canManageSource}
          permissionsLoading={permissions.loading}
        />
      )}

      {/*
        Suppressed while the namespace itself failed. Both queries are scoped to
        the same namespace, so one refusal denies both and this block would
        repeat the alert above it verbatim. The namespace error is the more
        fundamental of the two, so it is the one that gets reported.
      */}
      {!pageData.namespaceError &&
        stringGroupsData.error &&
        (isAccessDenied(stringGroupsData.error) ? (
          <AccessDeniedAlert
            message={getAccessDeniedMessage(stringGroupsData.error)}
            deniedNamespace={getAccessDeniedNamespace(stringGroupsData.error)}
            data-testid="string-groups-access-denied"
          />
        ) : (
          <Alert
            type="error"
            header={t('namespace-string-groups-load-error-header')}
          >
            {stringGroupsData.error.message}
          </Alert>
        ))}

      {!pageData.namespaceError && !stringGroupsData.error && (
        <StringGroupsTable
          namespaceId={resolvedNamespaceId}
          displayItems={stringGroupsData.displayItems}
          loading={stringGroupsData.initialLoading}
          isLoadingMore={stringGroupsData.isLoadingMore}
          hasMore={stringGroupsData.hasMore}
          handleLoadMore={stringGroupsData.handleLoadMore}
          hideCxFamilyColumn={!!pageData.namespace?.defaultCxFamily}
        />
      )}

      <CreateStringGroupModal
        visible={showCreateModal}
        onDismiss={() => setShowCreateModal(false)}
        onSubmit={handleCreateStringGroup}
        cxFamily={pageData.namespace?.defaultCxFamily}
        namespaceId={resolvedNamespaceId}
        loadedStringGroupIds={loadedStringGroupIds}
      />
    </SpaceBetween>
  );
};

export default NamespaceDetailPage;
